import React from "react";

import { toast } from "react-toastify";
import { makeAutoObservable, autorun } from "mobx";

import { RootStore, useStores } from "@stores";
import useVM from "@src/hooks/useVM";
import {
  Season,
  listSeasons,
  createSeason,
  updateSeason,
  deleteSeason
} from "@services/fieldsService";

class SeasonsStore {
  // Loading state.
  // Should be true while fetching list of seasons and current season
  // from the server.
  loading: boolean = true;
  // Allows to change loading state.
  private setLoading = (loading: boolean) => {
    this.loading = loading;
  }

  // List of seasons.
  seasons: Season[] = [];
  // Allows to change list of seasons.
  private setSeasons = (seasons: Season[]) => {
    this.seasons = seasons;
  }

  // Current season id.
  //
  // NOTE(nk2ge5k): there is a difference between current season on the
  // backend and this one - beacause this one should be set by the user to
  // view the data for the specific season and backend one is the season
  // that will receive the new data from the harvesters.
  currentSeasonId?: string;
  // Allows to change current season id
  setCurrentSeasonId = (id: string) => {
    this.currentSeasonId = id;
  }

  get currentSeason(): Season | null {
    return this.seasons.find((s) => s.id === this.currentSeasonId) || null;
  }

  constructor(private rootStore: RootStore) {
    makeAutoObservable(this);
    autorun(() => {
      if (this.rootStore.accountStore.authenticated) {
        this.loadSeasons()
          .then(() => {
            if (this.seasons.length === 0) {
              // No seasons yet - this is normal for new accounts.
              return;
            }
            // Pick the season marked as current on the backend,
            // falling back to the latest season (last in asc-sorted list).
            const current = this.seasons.find((s) => s.default)
              ?? this.seasons[this.seasons.length - 1];
            this.setCurrentSeasonId(current.id);
          })
          .catch((e) => {
            console.error("Failed to load seasons", e);
            toast.error("Failed to load seasons: " + e.toString())
          })
          .finally(() => {
            this.setLoading(false)
          });
      }
    });
  }

  // createSeason creates a new season for the user and returns its id.
  // copyFromSeasonId, when set, carries field boundaries + crops forward from
  // that older season into the new one.
  createSeason = async (from: Date, to: Date, copyFromSeasonId?: string): Promise<string> => {
    const id = await createSeason(from, to, copyFromSeasonId);

    // Refresh seasons list and switch to the newly created season.
    await this.loadSeasons();
    this.setCurrentSeasonId(id);

    return id;
  }

  // updateSeason edits an existing season's dates and refreshes the list.
  updateSeason = async (id: string, from: Date, to: Date): Promise<void> => {
    await updateSeason(id, { from, to });
    await this.loadSeasons();
  }

  // deleteSeason removes a season and reconciles the current selection.
  deleteSeason = async (id: string): Promise<void> => {
    await deleteSeason(id);
    await this.loadSeasons();
    if (this.currentSeasonId === id) {
      const fallback = this.seasons.find((s) => s.default)
        ?? this.seasons[this.seasons.length - 1];
      if (fallback) this.setCurrentSeasonId(fallback.id);
    }
  }

  private loadSeasons = async () => {
    return listSeasons().then((seasons) => {
      this.setSeasons(seasons);
    }).catch((e) => {
      // api.ts already toasted the server's message.
      console.error("Failed to load seasons list", e);
    });
  }
}

const ctx = React.createContext<SeasonsStore | null>(null);

export const useSeasonsVM = () => useVM(ctx);

export const useSeasonId = () => {
  const vm = useSeasonsVM();
  return vm.currentSeasonId;
}

export const ContextProvider: React.FC = ({ children }) => {
  const rootStore = useStores();
  return <ctx.Provider value={rootStore.seasonStore}>{children}</ctx.Provider>;
};

export default SeasonsStore;
